import { STROKE_REF_WIDTH, type Op, type OpLog, type Point, type StrokeOp } from '@panels/shared';

/**
 * The one stroke-to-bitmap function. Drawing, replay, the reveal, PNG export and
 * clipboard copy all go through here, so a comic looks identical everywhere.
 *
 * GUARDRAIL #1: panels are rendered from the op log on demand. Nothing raster is
 * ever the source of truth.
 * GUARDRAIL #2: color and width are read per op. Do not shortcut to black here.
 * GUARDRAIL #3: the stored points are raw. The smoothing below happens only at
 * render time and never touches what we keep.
 */

/** Draw ops before `op` in full, then op `op` up to and including point `point`. */
export interface RenderLimit {
  op: number;
  point: number;
}

export interface RenderOptions {
  /** Stop part-way through, for replay. Omit to draw everything. */
  limit?: RenderLimit;
  /** Fill first. Pass null for transparent (used when compositing). */
  background?: string | null;
  /** Render-time smoothing. On by default; raw points are unaffected either way. */
  smooth?: boolean;
  /**
   * Clear before drawing. Turn this off when the caller has already painted the
   * surface and is drawing under a pan/zoom transform, where clearing would punch
   * a hole in what is underneath.
   */
  clear?: boolean;
}

export function clearCanvas(ctx: CanvasRenderingContext2D, width: number, height: number, background: string | null = '#ffffff'): void {
  ctx.clearRect(0, 0, width, height);
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }
}

/**
 * `width`/`height` are the panel's size in CSS pixels. Points are normalized, so
 * this is the only place they become pixels (guardrail #4).
 */
export function renderOpLog(
  ctx: CanvasRenderingContext2D,
  log: OpLog | null,
  width: number,
  height: number,
  options: RenderOptions = {},
): void {
  const { limit, background = '#ffffff', smooth = true, clear = true } = options;
  if (clear) clearCanvas(ctx, width, height, background);
  if (!log) return;

  const lastOp = limit ? Math.min(limit.op, log.ops.length - 1) : log.ops.length - 1;

  for (let i = 0; i <= lastOp; i++) {
    const op = log.ops[i];
    if (!op) continue;
    const pointLimit = limit && i === limit.op ? limit.point : Infinity;
    drawOp(ctx, op, width, height, pointLimit, smooth);
  }
}

/** Dispatch over `kind`. Future op kinds (fill, erase) add cases here. */
function drawOp(
  ctx: CanvasRenderingContext2D,
  op: Op,
  width: number,
  height: number,
  pointLimit: number,
  smooth: boolean,
): void {
  switch (op.kind) {
    case 'stroke':
      drawStroke(ctx, op, width, height, pointLimit, smooth);
      break;
    default:
      // Unknown kinds are skipped rather than throwing: an older client should
      // still render whatever it does understand.
      break;
  }
}

/**
 * How much of a stroke shows when drawn up to `pointLimit`, and whether it shows
 * as a straight line. A straightened stroke is a line from its first point to
 * its latest one — but only from its snap onwards, so a replay shows the wobble
 * being drawn and then the snap (Round 2 §2.5).
 */
export function strokeShape(op: StrokeOp, pointLimit = Infinity): { count: number; straight: boolean } {
  const count = Math.max(0, Math.min(op.points.length, pointLimit + 1));
  const straight = !!op.snap && count > 1 && count - 1 >= op.snap.at;
  return { count, straight };
}

function drawStroke(
  ctx: CanvasRenderingContext2D,
  op: StrokeOp,
  width: number,
  height: number,
  pointLimit: number,
  smooth: boolean,
): void {
  const { points, color, width: strokeWidth } = op;
  const { count, straight } = strokeShape(op, pointLimit);
  if (count <= 0) return;

  // Widths scale with the panel exactly like the coordinates do.
  const lineWidth = Math.max(0.5, (strokeWidth * width) / STROKE_REF_WIDTH);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const px = (p: Point) => p[0] * width;
  const py = (p: Point) => p[1] * height;

  if (count === 1) {
    // A tap is a dot, not nothing.
    ctx.beginPath();
    ctx.arc(px(points[0]), py(points[0]), lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  ctx.beginPath();
  ctx.moveTo(px(points[0]), py(points[0]));

  if (straight) {
    // The raw points are all still there; a straightened stroke just draws the chord.
    ctx.lineTo(px(points[count - 1]), py(points[count - 1]));
  } else if (!smooth) {
    for (let i = 1; i < count; i++) ctx.lineTo(px(points[i]), py(points[i]));
  } else {
    // Quadratic through midpoints: cheap, stable, and it never invents geometry
    // beyond the captured path.
    for (let i = 1; i < count - 1; i++) {
      const cx = px(points[i]);
      const cy = py(points[i]);
      const mx = (cx + px(points[i + 1])) / 2;
      const my = (cy + py(points[i + 1])) / 2;
      ctx.quadraticCurveTo(cx, cy, mx, my);
    }
    ctx.lineTo(px(points[count - 1]), py(points[count - 1]));
  }

  ctx.stroke();
  ctx.restore();
}

/* ----------------------------------------------------------------- canvas */

/**
 * Size a canvas for the device pixel ratio and return a context already scaled to
 * CSS pixels, so callers can draw in CSS-pixel space and still get crisp lines.
 */
export function prepareCanvas(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
  dpr = window.devicePixelRatio || 1,
): CanvasRenderingContext2D | null {
  const targetW = Math.max(1, Math.round(cssWidth * dpr));
  const targetH = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width = targetW;
    canvas.height = targetH;
  }
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/** Render a panel to a detached canvas at an explicit scale (used by PNG export). */
export function renderToCanvas(log: OpLog | null, cssWidth: number, cssHeight: number, scale = 2): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cssWidth * scale);
  canvas.height = Math.round(cssHeight * scale);
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    renderOpLog(ctx, log, cssWidth, cssHeight);
  }
  return canvas;
}
